import * as React from "react"
import { cn } from "../../lib/utils"

const buttonVariants = {
  default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
  destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
  outline: "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
  secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
  ghost: "hover:bg-accent hover:text-accent-foreground",
  link: "text-primary underline-offset-4 hover:underline"
}

const buttonSizes = {
  default: "h-9 px-4 py-2",
  sm: "h-8 rounded-md px-3 text-xs",
  lg: "h-10 rounded-md px-8",
  icon: "h-9 w-9"
}

const Button = React.forwardRef(({ 
  className, 
  variant = "default",
  size = "default",
  asChild = false,
  ...props 
}, ref) => {
  const Comp = asChild ? React.Fragment : "button"
  return (
    <Comp
      className={cn(
        "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        buttonVariants[variant],
        buttonSizes[size],
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = "Button"

// Special variant for loading state
const ButtonLoading = React.forwardRef(({ 
  className, 
  children,
  loading = false,
  ...props 
}, ref) => {
  return (
    <Button
      ref={ref}
      className={cn("relative", className)}
      disabled={loading}
      {...props}
    >
      {loading && (
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <svg
            className="animate-spin h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </span>
      )}
      <span className={cn(loading && "invisible")}>
        {children}
      </span>
    </Button>
  )
})
ButtonLoading.displayName = "ButtonLoading"

// Icon button variant
const ButtonIcon = React.forwardRef(({ 
  className, 
  icon,
  ...props 
}, ref) => {
  return (
    <Button
      ref={ref}
      size="icon"
      className={cn("p-0", className)}
      {...props}
    >
      {icon}
    </Button>
  )
})
ButtonIcon.displayName = "ButtonIcon"

export { Button, ButtonLoading, ButtonIcon, buttonVariants }